import { keccak256, stringToHex } from "viem";
import type { InventoryResponse } from "../../../api/schemas";
import type { InventoryView, NftView, InventoryPayoutPreview } from "../types";
import { parseCanonicalNftTokenUri } from "../media/canonical-media";
import { trustedArtworkBaseUrl } from "../config/nft-artwork";
import { formatEth } from "../../../lib/financial";
import { sameAddress } from "../../../state/model-helpers";
import { inventoryItemSnapshot } from "./inventory-pagination";
import { normalizeReward } from "../../../api/reward-adapter";
import { ELYSIUM, isElysiumNftPolicy } from "../../../config/elysium";

function nftReward(item: NonNullable<InventoryResponse["items"]>[number]) {
  const reward = normalizeReward(item, item.status === "FINALIZED" || item.status === "BURNED");
  return item.chainId === ELYSIUM.chainId && !isElysiumNftPolicy(item.rewardPolicyVersion ?? "", item.rewardPolicyHash)
    ? { ...reward, rewardRate: null, rewardDenominator: null } : reward;
}

export function inventoryView(response: InventoryResponse | null, tokens: ReadonlyArray<{ address: string; symbol: string }>, wallet: string | null,
  loading: boolean, loadingMore: boolean,
  previews: Record<string, InventoryPayoutPreview>, projectContractsMissing = false,
  backendUnavailable = false, error: string | null = null): InventoryView {
  if (!response) return { loading, loadingMore, available: false, items: null, hasMore: false,
    blockingReasons: loading ? [] : backendUnavailable ? ["BACKEND_UNAVAILABLE"] :
      projectContractsMissing ? ["CONTRACT_NOT_DEPLOYED"] : [error ?? "INVENTORY_UNAVAILABLE"],
    freshnessLabel: loading ? "Loading canonical inventory" : backendUnavailable ? "BACKEND UNAVAILABLE" :
      projectContractsMissing ? "CONTRACT NOT DEPLOYED" : "Inventory unavailable" };
  const byAddress = new Map(tokens.map((token) => [token.address.toLowerCase(), token.symbol]));
  const items: NftView[] | null = response.items?.flatMap((item) => {
    if (!item.sourceToken || item.selectedTierUsd === null || item.status === "UNKNOWN") return [];
    const active = item.status !== "BURNED";
    const preview = previews[item.tokenId];
    const canonicalImage = canonicalImageForItem(item, inventoryItemSnapshot(item, response.snapshot));
    const reward = nftReward(item);
    return [{
      chainId: item.chainId,
      tokenId: item.tokenId,
      collectionAddress: item.collectionAddress,
      owner: item.owner,
      sourceToken: item.sourceToken,
      sourceSymbol: byAddress.get(item.sourceToken.toLowerCase()) ?? "TOKEN",
      tierUsd: item.selectedTierUsd,
      status: item.status,
      ...reward,
      rewardPolicyHash: item.rewardPolicyHash,
      mintFeeWei: item.mintFeeWei ?? null,
      rewardBps: reward.rewardPolicyVersion === "1" ? reward.rewardRate : null,
      rewardBand: item.bandIndex,
      imageUrl: canonicalImage?.imageUrl ?? null,
      artworkKind: canonicalImage?.artworkKind ?? null,
      imageKind: canonicalImage ? "CANONICAL_TOKEN_URI" :
        item.status === "PENDING_RANDOMNESS" || item.status === "RANDOMNESS_READY" ? "PENDING" : "UNAVAILABLE",
      mintTxHash: item.transactions.mint,
      finalizeTxHash: item.transactions.rewardFinalized,
      snapshotBlock: item.snapshot.blockNumber,
      randomnessStatus: item.status,
      // A finalized mirror row is necessary but not sufficient for a redeem.
      // Require an authenticated current route-specific preview: legacy API vault
      // payout, or the shared pinned fee quote after owner/state validation.
      redeemable: active && item.status === "FINALIZED" && Boolean(wallet && item.owner && sameAddress(wallet, item.owner)) &&
        preview?.redeemable === true,
      expectedPayoutWei: preview?.payoutWei ?? null,
      expectedPayoutEth: preview ? formatEth(BigInt(preview.payoutWei), 6) : null,
      redeemRoute: preview?.route ?? "LEGACY",
      ...(preview?.vaultPoolWei !== undefined ? { vaultPoolWei: preview.vaultPoolWei } : {}),
      ...(preview?.poolAfterSweepWei !== undefined ? { poolAfterSweepWei: preview.poolAfterSweepWei } : {}),
    } satisfies NftView];
  }) ?? null;
  return { loading, loadingMore, available: response.inventoryAvailable, items,
    hasMore: response.nextCursor !== null,
    blockingReasons: response.blockingReasons, freshnessLabel: response.snapshot ?
      `First page #${response.snapshot.blockNumber} · ${response.dataStatus}` : response.dataStatus };
}

type ValidatedArtwork = Pick<import("../media/canonical-media").CanonicalNftMedia, "imageUrl" | "artworkKind">;
const canonicalImageCache = new WeakMap<object, { snapshot: string; image: ValidatedArtwork | null }>();
/** Detail media has its own committed read snapshot, separate from the last lifecycle event. */
export function selectedNftView(item: NonNullable<InventoryResponse["items"]>[number], sourceSymbol: string): NftView | null {
  if (!item.sourceToken || item.selectedTierUsd === null || item.status === "UNKNOWN") return null;
  if (item.rewardBps !== null && (!Number.isInteger(item.rewardBps) || item.rewardBps < 0 || item.rewardBps > 10_000)) return null;
  if (item.canonicalMedia.snapshot && !Number.isSafeInteger(item.canonicalMedia.snapshot.blockNumber)) return null;
  const image = canonicalImageForItem(item, item.canonicalMedia.snapshot);
  const reward = nftReward(item);
  return { chainId: item.chainId, tokenId: item.tokenId, collectionAddress: item.collectionAddress, owner: item.owner,
    sourceToken: item.sourceToken, sourceSymbol, tierUsd: item.selectedTierUsd, status: item.status,
    ...reward,
    rewardPolicyHash: item.rewardPolicyHash,
    mintFeeWei: item.mintFeeWei ?? null,
    rewardBps: reward.rewardPolicyVersion === "1" ? reward.rewardRate : null,
    rewardBand: item.bandIndex, imageUrl: image?.imageUrl ?? null, artworkKind: image?.artworkKind ?? null,
    imageKind: image ? "CANONICAL_TOKEN_URI" : "UNAVAILABLE",
    mintTxHash: item.transactions.mint, finalizeTxHash: item.transactions.rewardFinalized,
    snapshotBlock: item.canonicalMedia.snapshot?.blockNumber ?? item.snapshot.blockNumber, randomnessStatus: item.status,
    redeemable: false, expectedPayoutWei: null, expectedPayoutEth: null };
}
function canonicalImageForItem(item: NonNullable<InventoryResponse["items"]>[number],
  snapshot: InventoryResponse["snapshot"]): ValidatedArtwork | null {
  const key = JSON.stringify([snapshot, trustedArtworkBaseUrl()]);
  const cached = canonicalImageCache.get(item);
  if (cached?.snapshot === key) return cached.image;
  const image = validateCanonicalImage(item, snapshot);
  canonicalImageCache.set(item, { snapshot: key, image });
  return image;
}

function validateCanonicalImage(item: NonNullable<InventoryResponse["items"]>[number],
  inventorySnapshot: InventoryResponse["snapshot"]): ValidatedArtwork | null {
  const media = item.canonicalMedia;
  if (item.chainId === ELYSIUM.chainId && !isElysiumNftPolicy(item.rewardPolicyVersion ?? "", item.rewardPolicyHash)) return null;
  if (!media.available || media.dataStatus !== "CURRENT" || media.blockingReasons.length !== 0 ||
    media.chainId !== item.chainId || !sameAddress(media.collectionAddress, item.collectionAddress) ||
    media.tokenId !== item.tokenId || !inventorySnapshot || !media.snapshot ||
    media.snapshot.blockNumber !== inventorySnapshot.blockNumber ||
    media.snapshot.blockHash.toLowerCase() !== inventorySnapshot.blockHash.toLowerCase() ||
    !item.sourceToken || item.selectedTierUsd === null || item.status === "BURNED" || item.status === "UNKNOWN") return null;
  try {
    const reward = normalizeReward(item, item.status === "FINALIZED");
    if (item.rewardPolicyVersion === "2" && (reward.rewardDenominator !== 1_000_000 ||
      item.status === "FINALIZED" && reward.rewardRate === null)) return null;
    if (keccak256(stringToHex(media.tokenUri)).toLowerCase() !== media.tokenUriHash.toLowerCase()) return null;
    const parsed = parseCanonicalNftTokenUri(media.tokenUri, {
      tokenId: item.tokenId,
      status: item.status,
      rewardBps: item.rewardBps,
      ...(item.rewardPolicyVersion === "2" ? { rewardPolicyVersion: "2", rewardRate: reward.rewardRate,
        rewardDenominator: reward.rewardDenominator } : {}),
      sourceToken: item.sourceToken,
      tierUsd: item.selectedTierUsd,
      chainId: item.chainId,
      collectionAddress: item.collectionAddress,
      artworkBaseUrl: trustedArtworkBaseUrl(),
    });
    if (parsed.name !== media.metadata.name || parsed.description !== media.metadata.description ||
      parsed.rewardStatus !== media.metadata.rewardStatus ||
      parsed.rewardBps !== media.metadata.rewardBps || parsed.imageUrl !== media.image.url ||
      item.rewardPolicyVersion === "2" && (media.metadata.rewardPpm !== parsed.rewardRate ||
        media.metadata.rewardDenominator !== parsed.rewardDenominator) ||
      (parsed.imageUrl.startsWith("data:") ? media.image.mediaType !== "image/svg+xml" ||
        !["base64", "utf8"].includes(media.image.encoding) :
        media.image.mediaType !== "image/png" || media.image.encoding !== "url")) return null;
    return { imageUrl: parsed.imageUrl, artworkKind: parsed.artworkKind };
  } catch {
    return null;
  }
}
